"use client";

import {
  forwardRef,
  useCallback,
  useEffect,
  useId,
  useImperativeHandle,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Control, Controller } from "react-hook-form";
import type { Ctx } from "@milkdown/ctx";
import {
  Editor,
  defaultValueCtx,
  editorViewCtx,
  parserCtx,
  prosePluginsCtx,
  rootCtx,
  editorViewOptionsCtx,
  commandsCtx,
  serializerCtx,
  type CmdKey,
} from "@milkdown/core";
import { Slice } from "@milkdown/prose/model";
import {
  Plugin,
  PluginKey,
  TextSelection,
  type EditorState,
} from "@milkdown/prose/state";
import {
  commonmark,
  remarkPreserveEmptyLinePlugin,
  toggleStrongCommand,
  toggleEmphasisCommand,
  createCodeBlockCommand,
  turnIntoTextCommand,
  wrapInBulletListCommand,
  liftListItemCommand,
  strongSchema,
  emphasisSchema,
} from "@milkdown/preset-commonmark";
import { history } from "@milkdown/plugin-history";
import { listener, listenerCtx } from "@milkdown/plugin-listener";
import { clipboard } from "@milkdown/plugin-clipboard";
import { dropCursor } from "@milkdown/prose/dropcursor";
import { $prose } from "@milkdown/utils";
import { Milkdown, MilkdownProvider, useEditor } from "@milkdown/react";
import "./milkdown.css";
import { NoxusFormLabel } from "@/components/Form/NoxusFormLabel";
import { Textarea } from "@/components/shadcn/textarea";
import { cn } from "@/lib/utils";
import { useEditorStoreSync } from "./useEditorStoreSync";
import type { EditorInput } from "./types";
import {
  editorIdCtx,
  insertVariableCommand,
  variableSchema,
  variablePlugin,
} from "./variablePlugin";
import {
  insertTemplateCommand,
  templatePlugin,
  templateSchema,
  templateTokensEnabledCtx,
} from "./templatePlugin";
import { parseTemplateVariableName } from "@/components/SpotflowV2/Sidebar/variableRefs";
import { unescapeVarDirectiveMarkers } from "./normalizeVarDirectives";
import { Toolbar } from "./Toolbar";
import { ComplexMarkdownDialog } from "./ComplexMarkdownDialog";
import {
  filterVariableOptions,
  flattenVariableOptions,
  VariablePickerPanel,
} from "@/components/SpotflowV2/Sidebar/VariablePickerPanel";
import { inputsToNodeOptions } from "./inputsToNodeOptions";

export { inputsToNodeOptions };

export interface ComplexMarkdownEditorHandle {
  insertVariable: (id: string) => void;
}

// The commonmark preset bundles `remarkPreserveEmptyLinePlugin`, which makes
// the paragraph serializer emit `<br />` for empty paragraphs so the visual
// blank line survives a roundtrip. We want plain markdown output, so we strip
// that plugin — empty paragraphs then collapse to a normal `\n\n` break.
const commonmarkPlain = commonmark.filter(
  (p) =>
    p !== remarkPreserveEmptyLinePlugin.plugin &&
    p !== remarkPreserveEmptyLinePlugin.options
);

// remark-stringify terminates every serialized document with "\n" — a
// serializer artifact, not user content (a trailing blank line can't survive a
// markdown round-trip anyway), so it must never reach the stored value.
export const stripSerializerNewline = (markdown: string) =>
  markdown.replace(/\n$/, "");

export const normalizeSerializedMarkdown = (markdown: string) =>
  unescapeVarDirectiveMarkers(stripSerializerNewline(markdown));

// Shows a visible caret at the drop position while dragging chips, so the user
// can tell exactly where the chip will land.
const dropCursorPlugin = $prose(() =>
  dropCursor({ color: "var(--ring)", width: 2 })
);

// Same hint the v1 rich-text fields show under their editor.
export function SlashVariableHint() {
  return (
    <span className="block text-xs font-regular leading-normal text-muted-foreground mt-1.5 whitespace-pre-line">
      Insert{" "}
      <span className="font-semibold text-foreground bg-muted rounded-sm px-1.5">
        /
      </span>{" "}
      for a variable value.
    </span>
  );
}

interface ComplexMarkdownFieldProps {
  control: Control<Record<string, unknown>>;
  name: string;
  label?: string;
  tooltip?: string;
  placeholder?: string;
  isDisabled?: boolean;
  inputs: EditorInput[];
  invalid?: boolean;
  onBlur?: () => void;
  nodeId?: string;
  variableBindings?: Record<string, string | undefined>;
  minRows?: number;
  maxRows?: number;
  hideRawToggle?: boolean;
  disableVariables?: boolean;
  immediateUpdates?: boolean;
}

export function ComplexMarkdownField(props: ComplexMarkdownFieldProps) {
  const [isExpanded, setIsExpanded] = useState(false);
  return (
    <Controller
      control={props.control}
      name={props.name}
      render={({ field: { value, onChange, onBlur: fieldOnBlur } }) => {
        // Legacy rich-text configs store `[{text, definition}]` TipTap lists;
        // unwrap to the first entry's text (parity with the backend coercers).
        const rawValue = Array.isArray(value) ? value[0] : value;
        const stringValue =
          typeof rawValue === "string"
            ? rawValue
            : rawValue &&
                typeof rawValue === "object" &&
                "text" in rawValue &&
                typeof (rawValue as { text: unknown }).text === "string"
              ? (rawValue as { text: string }).text
              : "";
        const handleBlur = () => {
          fieldOnBlur();
          props.onBlur?.();
        };
        return (
          <div className="w-full">
            {props.label && (
              <NoxusFormLabel
                label={props.label}
                tooltip={props.tooltip}
                name={props.name}
                isDisabled={props.isDisabled}
              />
            )}
            <MilkdownProvider>
              <ComplexMarkdownEditor
                value={stringValue}
                onChange={onChange}
                placeholder={props.placeholder}
                isDisabled={props.isDisabled}
                inputs={props.inputs}
                invalid={props.invalid}
                onBlur={handleBlur}
                nodeId={props.nodeId}
                variableBindings={props.variableBindings}
                minRows={props.minRows}
                maxRows={props.maxRows}
                hideRawToggle={props.hideRawToggle}
                disableVariables={props.disableVariables}
                immediateUpdates={props.immediateUpdates}
                onExpand={
                  props.isDisabled ? undefined : () => setIsExpanded(true)
                }
              />
            </MilkdownProvider>
            {props.inputs.length > 0 && <SlashVariableHint />}
            <ComplexMarkdownDialog
              isOpen={isExpanded}
              onOpenChange={setIsExpanded}
              initialValue={stringValue}
              onCommit={onChange}
              inputs={props.inputs}
              label={props.label}
              placeholder={props.placeholder}
              isDisabled={props.isDisabled}
              hideRawToggle={props.hideRawToggle}
              disableVariables={props.disableVariables}
            />
          </div>
        );
      }}
    />
  );
}

interface EditorSurfaceProps {
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  isDisabled?: boolean;
  inputs: EditorInput[];
  // When set, renders an "expand" button in the toolbar. Used by the field to
  // open a dialog-size version of itself. Hidden inside the dialog.
  onExpand?: () => void;
  // When true, the editor expands to fill its parent's available height
  // instead of using a fixed min-height. Used inside the expand dialog.
  fillHeight?: boolean;
  invalid?: boolean;
  onBlur?: () => void;
  nodeId?: string;
  variableBindings?: Record<string, string | undefined>;
  // Optional content rendered at the start of the toolbar's right group.
  // Used by AIComplexMarkdownField to mount the heuristic score badge.
  toolbarExtra?: React.ReactNode;
  hideToolbar?: boolean;
  hideRawToggle?: boolean;
  minRows?: number;
  maxRows?: number;
  // Tightens vertical padding so a single-row editor matches the height of a
  // regular <Input> (h-8 = 2rem: 1px borders + 4.5px padding + one 1.3125rem
  // line). Used for list-item rows that should read as plain inputs.
  compact?: boolean;
  // Focuses the editor once it finishes mounting (Milkdown creates the
  // ProseMirror view asynchronously, so the native attribute can't work).
  autoFocus?: boolean;
  // Promotes {{...}} template tokens to chip nodes (serialized back to the
  // literal token). Only for fields whose backend substitutes such templates
  // itself (browser playbook steps); everywhere else user-typed braces are
  // plain text and must stay unstyled.
  templateChips?: boolean;
  // A literal-only editor: `/` is just a slash and no variable menu opens.
  // The Input node's fixed value is a constant, so it opts out here.
  disableVariables?: boolean;
  immediateUpdates?: boolean;
}

// The editor content is 0.875rem text; each paragraph line occupies its
// line-height (1.5 = 1.3125rem) plus a 0.5em (0.4375rem) bottom margin, except
// the last paragraph whose margin is stripped (see milkdown.css). Padding is
// py-2 (1rem total), or 9px total in compact mode. Convert rows to rem.
function editorRowsToRem(rows: number, compact: boolean): string {
  const content = rows * 1.3125 + (rows - 1) * 0.4375;
  return `${(content + (compact ? 0.5625 : 1)).toFixed(4)}rem`;
}

// Slash state is only ever opened by the user typing `/`. Focus stays in the
// editor the whole time: the `/query` text lives in the document and `query`
// is re-derived from it (see refreshSlash), while the menu is a non-focusable
// popup. It closes when the anchor `/` disappears, the caret leaves the `/`'s
// textblock or moves before it, Escape is pressed, or focus leaves the editor.
// Open/refresh/close happen synchronously in a ProseMirror plugin view (see
// createSlashPlugin) — the milkdown listener plugin debounces doc updates
// ~200ms and coalesces transactions, which made the menu laggy and miss a
// delete-`/`-then-retype (net doc diff of zero).
type SlashState =
  | { open: false }
  | {
      open: true;
      slashPos: number;
      x: number;
      // Bottom and top of the caret's rect — the menu anchors below the
      // caret by default and flips above it when the viewport runs out.
      y: number;
      caretTop: number;
      query: string;
    };

const SLASH_MENU_WIDTH = 330;
const SLASH_MENU_MARGIN = 8;

export const ComplexMarkdownEditor = forwardRef<
  ComplexMarkdownEditorHandle,
  EditorSurfaceProps
>(function ComplexMarkdownEditor(
  {
    value,
    onChange,
    placeholder,
    isDisabled,
    inputs,
    onExpand,
    fillHeight,
    invalid,
    onBlur,
    nodeId,
    variableBindings,
    toolbarExtra,
    hideToolbar,
    hideRawToggle,
    minRows,
    maxRows,
    compact,
    autoFocus,
    templateChips,
    disableVariables,
    immediateUpdates,
  }: EditorSurfaceProps,
  ref
) {
  const immediateUpdatesRef = useRef(immediateUpdates);
  immediateUpdatesRef.current = immediateUpdates;
  const templateChipsRef = useRef(templateChips);
  templateChipsRef.current = templateChips;
  const disableVariablesRef = useRef(disableVariables);
  disableVariablesRef.current = disableVariables;
  const reactId = useId();
  const editorId = useMemo(
    () => `complex-md-${reactId.replace(/:/g, "_")}`,
    [reactId]
  );

  const onChangeRef = useRef(onChange);
  useEffect(() => {
    onChangeRef.current = onChange;
  }, [onChange]);

  const onBlurRef = useRef(onBlur);
  useEffect(() => {
    onBlurRef.current = onBlur;
  }, [onBlur]);

  const lastValueRef = useRef(value);
  const valueRef = useRef(value);
  useEffect(() => {
    valueRef.current = value;
  }, [value]);

  const [slash, setSlashState] = useState<SlashState>({ open: false });
  const [slashIndex, setSlashIndex] = useState(0);
  const [isRaw, setIsRaw] = useState(false);
  // Guards the (debounced) Milkdown listeners: without it every raw-mode
  // keystroke round-trips through the hidden editor and comes back as
  // onChange(normalized markdown), reverting the edit.
  const isRawRef = useRef(isRaw);
  const [isBoldActive, setIsBoldActive] = useState(false);
  const [isItalicActive, setIsItalicActive] = useState(false);
  const [isCodeBlockActive, setIsCodeBlockActive] = useState(false);
  const [isBulletListActive, setIsBulletListActive] = useState(false);

  const variableOptions = useMemo(() => inputsToNodeOptions(inputs), [inputs]);

  useEditorStoreSync(editorId, inputs, nodeId, variableBindings);

  const filteredSlashOptions = useMemo(() => {
    if (!slash.open) return [];
    return filterVariableOptions(variableOptions, slash.query);
  }, [slash, variableOptions]);

  const filteredSlashItems = useMemo(
    () => flattenVariableOptions(filteredSlashOptions),
    [filteredSlashOptions]
  );

  // The ref is written eagerly (not via effect) so ProseMirror handlers that
  // fire several transactions within one frame never read a stale state.
  const slashRef = useRef(slash);
  const setSlash = useCallback((next: SlashState) => {
    slashRef.current = next;
    setSlashState(next);
  }, []);
  const slashIndexRef = useRef(slashIndex);
  useEffect(() => {
    slashIndexRef.current = slashIndex;
  }, [slashIndex]);
  const filteredRef = useRef(filteredSlashItems);
  useEffect(() => {
    filteredRef.current = filteredSlashItems;
  }, [filteredSlashItems]);

  const slashKey = slash.open ? slash.query : null;
  useEffect(() => {
    setSlashIndex(0);
  }, [slashKey]);

  // Keep the fixed-position menu inside the viewport: the horizontal clamp is
  // pure math (the width is constant), but the vertical flip needs the
  // rendered height, so it's measured here before paint and the menu flips
  // above the caret when it would overflow the bottom edge.
  const slashMenuRef = useRef<HTMLDivElement | null>(null);
  const [slashMenuTop, setSlashMenuTop] = useState<number | null>(null);
  useLayoutEffect(() => {
    if (!slash.open) {
      setSlashMenuTop(null);
      return;
    }
    const el = slashMenuRef.current;
    if (!el) return;
    const height = el.offsetHeight;
    let top = slash.y + 4;
    if (top + height > window.innerHeight - SLASH_MENU_MARGIN) {
      top = slash.caretTop - height - 4;
      if (top < SLASH_MENU_MARGIN) {
        top = Math.max(
          SLASH_MENU_MARGIN,
          window.innerHeight - height - SLASH_MENU_MARGIN
        );
      }
    }
    setSlashMenuTop(top);
  }, [slash, filteredSlashItems]);

  const commitSlashRef = useRef<(id: string) => void>(() => {});

  const { get, loading } = useEditor(
    (root) => {
      return Editor.make()
        .config((ctx) => {
          ctx.set(rootCtx, root);
          ctx.set(defaultValueCtx, value);
          ctx.set(editorIdCtx.key, editorId);
          ctx.set(templateTokensEnabledCtx.key, !!templateChipsRef.current);
          ctx.update(editorViewOptionsCtx, (prev) => ({
            ...prev,
            editable: () => !isDisabled,
            attributes: {
              class: cn(
                "milkdown-editor focus:outline-none px-3",
                compact ? "py-[4.5px]" : "py-2",
                !fillHeight && minRows == null && "min-h-[120px]"
              ),
              ...(minRows != null && !fillHeight
                ? {
                    style: `min-height: ${editorRowsToRem(minRows, !!compact)}`,
                  }
                : {}),
              "data-placeholder": placeholder ?? "",
            },
            // When the menu is open, intercept arrow/enter/tab/escape.
            handleKeyDown: (_view, event) => {
              const s = slashRef.current;
              if (!s.open) return false;
              const items = filteredRef.current;
              if (event.key === "Escape") {
                setSlash({ open: false });
                return true;
              }
              if (items.length === 0) return false;
              if (event.key === "ArrowDown") {
                setSlashIndex((i) => (i + 1) % items.length);
                return true;
              }
              if (event.key === "ArrowUp") {
                setSlashIndex((i) => (i - 1 + items.length) % items.length);
                return true;
              }
              if (event.key === "Enter" || event.key === "Tab") {
                const picked = items[slashIndexRef.current];
                if (picked) commitSlashRef.current(picked.value);
                return true;
              }
              return false;
            },
          }));
          ctx.update(prosePluginsCtx, (plugins) => [
            ...plugins,
            new Plugin({
              view: () => ({
                update(view, previousState) {
                  if (!immediateUpdatesRef.current || isRawRef.current) return;
                  if (view.state.doc.eq(previousState.doc)) return;
                  const serialize = ctx.get(serializerCtx);
                  const markdown = normalizeSerializedMarkdown(
                    serialize(view.state.doc)
                  );
                  if (markdown === lastValueRef.current) return;
                  lastValueRef.current = markdown;
                  onChangeRef.current(markdown);
                },
              }),
            }),
            createSlashPlugin(slashRef, setSlash, disableVariablesRef),
            createToolbarStatePlugin(ctx, {
              setBold: setIsBoldActive,
              setItalic: setIsItalicActive,
              setCodeBlock: setIsCodeBlockActive,
              setBulletList: setIsBulletListActive,
            }),
          ]);
          const l = ctx.get(listenerCtx);
          l.markdownUpdated((_ctx, markdown) => {
            if (isRawRef.current || immediateUpdatesRef.current) return;
            // The stripped value must also land in lastValueRef: the parse-back
            // effect compares it to the value prop, and a mismatch would
            // re-parse the doc on every keystroke.
            const clean = normalizeSerializedMarkdown(markdown);
            lastValueRef.current = clean;
            onChangeRef.current(clean);
          });
          l.blur((ctx) => {
            try {
              if (!isRawRef.current) {
                const view = ctx.get(editorViewCtx);
                const serializer = ctx.get(serializerCtx);
                const markdown = normalizeSerializedMarkdown(
                  serializer(view.state.doc)
                );
                if (markdown !== lastValueRef.current) {
                  lastValueRef.current = markdown;
                  onChangeRef.current(markdown);
                }
              }
            } catch {
              // Editor may be tearing down during unmount.
            }
            // Focus never moves into the menu (it's a non-focusable popup and
            // its buttons preventDefault on mousedown), so a blur always means
            // the user left the field: close the menu and report the blur.
            setSlash({ open: false });
            onBlurRef.current?.();
          });
        })
        .use(commonmarkPlain)
        .use(history)
        .use(listener)
        .use(clipboard)
        .use(dropCursorPlugin)
        .use(variablePlugin)
        .use(templatePlugin);
    },
    [editorId]
  );

  // Once per mount: `get` has a fresh identity every render, so without the
  // ref guard this would re-run — and steal focus back — on any re-render.
  const didAutoFocusRef = useRef(false);
  useEffect(() => {
    if (!autoFocus || loading || didAutoFocusRef.current) return;
    didAutoFocusRef.current = true;
    get()?.action((ctx) => {
      ctx.get(editorViewCtx).focus();
    });
  }, [autoFocus, loading, get]);

  useEffect(() => {
    // In raw mode the hidden doc must not chase the value — parsing it would
    // fire markdownUpdated → onChange(normalized), overwriting the user's
    // edits. The doc catches up when this effect re-runs on leaving raw mode.
    if (isRaw) return;
    if (value === lastValueRef.current) return;
    const editor = get();
    if (!editor) return;
    editor.action((ctx) => {
      const view = ctx.get(editorViewCtx);
      const parser = ctx.get(parserCtx);
      const doc = parser(value);
      if (!doc) return;
      const { state } = view;
      const tr = state.tr.replace(
        0,
        state.doc.content.size,
        new Slice(doc.content, 0, 0)
      );
      view.dispatch(tr);
      lastValueRef.current = value;
    });
  }, [value, get, isRaw]);

  const insertVariableFromBar = useCallback(
    (id: string) => {
      const editor = get();
      if (!editor) return;
      editor.action((ctx) => {
        const view = ctx.get(editorViewCtx);
        view.focus();
        const templateName = parseTemplateVariableName(id);
        if (templateName !== null) {
          ctx
            .get(commandsCtx)
            .call(insertTemplateCommand.key, { name: templateName });
          return;
        }
        ctx.get(commandsCtx).call(insertVariableCommand.key, { id });
      });
    },
    [get]
  );

  useImperativeHandle(ref, () => ({ insertVariable: insertVariableFromBar }), [
    insertVariableFromBar,
  ]);

  const callCommand = useCallback(
    (pickKey: (state: EditorState) => CmdKey<unknown>) => {
      const editor = get();
      if (!editor) return;
      editor.action((ctx) => {
        const view = ctx.get(editorViewCtx);
        view.focus();
        ctx.get(commandsCtx).call(pickKey(view.state));
      });
    },
    [get]
  );

  const toggleBold = useCallback(
    () => callCommand(() => toggleStrongCommand.key),
    [callCommand]
  );
  const toggleItalic = useCallback(
    () => callCommand(() => toggleEmphasisCommand.key),
    [callCommand]
  );
  const toggleCodeBlock = useCallback(
    () =>
      callCommand((state) =>
        isInCodeBlock(state)
          ? turnIntoTextCommand.key
          : createCodeBlockCommand.key
      ),
    [callCommand]
  );
  const toggleBulletList = useCallback(
    () =>
      callCommand((state) =>
        isInBulletList(state)
          ? liftListItemCommand.key
          : wrapInBulletListCommand.key
      ),
    [callCommand]
  );

  const toggleRaw = useCallback(() => {
    setSlash({ open: false });
    const next = !isRaw;
    // Eager (not via effect) so the debounced listeners never see a stale
    // mode between the click and the re-render.
    isRawRef.current = next;
    setIsRaw(next);
  }, [setSlash, isRaw]);

  const getMarkdownValue = useCallback(() => valueRef.current ?? "", []);

  const commitSlash = useCallback(
    (id: string) => {
      const editor = get();
      if (!editor) return;
      const s = slashRef.current;
      if (!s.open) return;
      editor.action((ctx) => {
        const view = ctx.get(editorViewCtx);
        const state = view.state;
        // The `/query` text lives in the document, so the whole range from the
        // `/` to the caret is replaced by the chip.
        const from = s.slashPos;
        const to = Math.max(s.slashPos + 1, state.selection.from);
        const templateName = parseTemplateVariableName(id);
        const chip =
          templateName !== null
            ? templateSchema.type(ctx).create({ name: templateName })
            : variableSchema.type(ctx).create({ id });
        const space = state.schema.text(" ");
        let tr = state.tr.replaceWith(from, to, chip);
        const afterChip = from + chip.nodeSize;
        tr = tr.insert(afterChip, space);
        const cursorPos = afterChip + space.nodeSize;
        tr = tr.setSelection(TextSelection.create(tr.doc, cursorPos));
        view.dispatch(tr.scrollIntoView());
        view.focus();
      });
      setSlash({ open: false });
    },
    [get, setSlash]
  );

  useEffect(() => {
    commitSlashRef.current = commitSlash;
  }, [commitSlash]);

  return (
    <div
      className={cn(
        "relative flex flex-col rounded-md border bg-background overflow-hidden",
        invalid
          ? "border-warning-400 focus-within:ring-1 focus-within:ring-warning-400/70"
          : "border-input focus-within:ring-1 focus-within:ring-ring",
        fillHeight && "h-full flex-1",
        isDisabled && "opacity-60 pointer-events-none"
      )}
    >
      {!hideToolbar && (
        <Toolbar
          variableOptions={variableOptions}
          showVariablePicker={inputs.length > 0}
          onInsertVariable={insertVariableFromBar}
          onToggleBold={toggleBold}
          onToggleItalic={toggleItalic}
          onToggleCodeBlock={toggleCodeBlock}
          onToggleBulletList={toggleBulletList}
          isBold={isBoldActive}
          isItalic={isItalicActive}
          isCodeBlock={isCodeBlockActive}
          isBulletList={isBulletListActive}
          isRaw={isRaw}
          onToggleRaw={toggleRaw}
          hideRawToggle={hideRawToggle}
          getMarkdown={getMarkdownValue}
          onExpand={onExpand}
          isDisabled={isDisabled}
          extraContent={toolbarExtra}
        />
      )}
      <div
        className={cn(
          fillHeight && "noxus-markdown-editor-fill flex-1 min-h-0",
          maxRows != null && "overflow-y-auto",
          isRaw && "hidden"
        )}
        style={
          maxRows != null
            ? { maxHeight: editorRowsToRem(maxRows, !!compact) }
            : undefined
        }
        onMouseDown={(e) => {
          if (!fillHeight || isDisabled) return;
          const target = e.target;
          if (!(target instanceof Element)) return;
          if (target.closest(".ProseMirror")) return;
          const editor = get();
          if (!editor) return;
          e.preventDefault();
          editor.action((ctx) => {
            const view = ctx.get(editorViewCtx);
            const { state } = view;
            const sel = TextSelection.atEnd(state.doc);
            view.dispatch(state.tr.setSelection(sel));
            view.focus();
          });
        }}
      >
        <Milkdown />
      </div>
      {isRaw && (
        <Textarea
          className={cn(
            "w-full resize-none rounded-none border-none bg-transparent px-3 py-2 font-mono text-xs shadow-none focus-visible:ring-0",
            fillHeight ? "flex-1 min-h-0" : "min-h-[120px]"
          )}
          value={value}
          disabled={isDisabled}
          onChange={(e) => onChange(e.target.value)}
          onBlur={onBlur}
          placeholder={placeholder}
        />
      )}
      {slash.open && (
        <div
          ref={slashMenuRef}
          data-slash-menu
          role="listbox"
          className="fixed z-50 w-[330px] rounded-md border bg-popover text-popover-foreground shadow-md overflow-hidden"
          style={{
            left: Math.max(
              SLASH_MENU_MARGIN,
              Math.min(
                slash.x,
                window.innerWidth - SLASH_MENU_WIDTH - SLASH_MENU_MARGIN
              )
            ),
            top: slashMenuTop ?? slash.y + 4,
          }}
          onMouseDown={(e) => e.preventDefault()}
        >
          <VariablePickerPanel
            options={variableOptions}
            search={slash.query}
            showSearch={false}
            activeIndex={Math.min(
              slashIndex,
              Math.max(filteredSlashItems.length - 1, 0)
            )}
            onActiveIndexChange={setSlashIndex}
            onSelect={({ value: id }) => commitSlash(id)}
          />
        </div>
      )}
    </div>
  );
});

interface ToolbarStateSetters {
  setBold: (v: boolean) => void;
  setItalic: (v: boolean) => void;
  setCodeBlock: (v: boolean) => void;
  setBulletList: (v: boolean) => void;
}

// Falls back to stored marks on empty selections (so a newly-typed letter inherits the mark).
function updateActiveMarks(
  ctx: Ctx,
  state: EditorState,
  { setBold, setItalic }: ToolbarStateSetters
) {
  const strongType = strongSchema.type(ctx);
  const emphasisType = emphasisSchema.type(ctx);
  const { from, to, empty, $head } = state.selection;
  if (empty) {
    const marks = state.storedMarks ?? $head.marks();
    setBold(!!strongType.isInSet(marks));
    setItalic(!!emphasisType.isInSet(marks));
  } else {
    setBold(state.doc.rangeHasMark(from, to, strongType));
    setItalic(state.doc.rangeHasMark(from, to, emphasisType));
  }
}

// The `/` menu triggers anywhere a text token can be typed, including
// mid-word — the only exclusion is code, where `/` is literal (file paths,
// regex, etc.): inside a code block or under the inline-code mark.
function isSlashTriggerContext(state: EditorState, slashPos: number): boolean {
  const $pos = state.doc.resolve(slashPos);
  if (!$pos.parent.isTextblock) return false;
  if ($pos.parent.type.name === "code_block") return false;
  const inlineCode = state.schema.marks.inlineCode;
  if (
    inlineCode &&
    state.doc.rangeHasMark(slashPos, slashPos + 1, inlineCode)
  ) {
    return false;
  }
  return true;
}

// Closes the menu when its anchor `/` no longer exists, the selection left the
// `/`'s textblock, or the caret moved before it; otherwise re-derives the
// query from the document text between the `/` and the caret. Focus never
// leaves the editor — the query lives in the doc, not a separate input.
function refreshSlash(
  state: EditorState,
  s: SlashState & { open: true },
  setSlash: (next: SlashState) => void
) {
  const sel = state.selection;
  const slashEnd = s.slashPos + 1;
  if (
    !sel.empty ||
    sel.from < slashEnd ||
    slashEnd > state.doc.content.size ||
    state.doc.textBetween(s.slashPos, slashEnd) !== "/"
  ) {
    setSlash({ open: false });
    return;
  }
  const $slash = state.doc.resolve(s.slashPos);
  const $caret = state.doc.resolve(sel.from);
  if (!$slash.sameParent($caret)) {
    setSlash({ open: false });
    return;
  }
  const query = state.doc.textBetween(slashEnd, sel.from);
  // A space ends the slash query — the user is writing prose, not picking a
  // variable, so dismiss and leave the typed text in the document.
  if (/\s/.test(query)) {
    setSlash({ open: false });
    return;
  }
  if (query !== s.query) setSlash({ ...s, query });
}

const slashPluginKey = new PluginKey("COMPLEX_MD_SLASH");
const toolbarStatePluginKey = new PluginKey("COMPLEX_MD_TOOLBAR_STATE");

function isInCodeBlock(state: EditorState): boolean {
  return state.selection.$from.parent.type.name === "code_block";
}

function isInBulletList(state: EditorState): boolean {
  const { $from } = state.selection;
  for (let depth = $from.depth; depth > 0; depth--) {
    if ($from.node(depth).type.name === "bullet_list") return true;
  }
  return false;
}

// Runs after every transaction, since undo or a block-type change can leave the selection unmoved.
function createToolbarStatePlugin(
  ctx: Ctx,
  setters: ToolbarStateSetters
): Plugin {
  const sync = (state: EditorState) => {
    updateActiveMarks(ctx, state, setters);
    setters.setCodeBlock(isInCodeBlock(state));
    setters.setBulletList(isInBulletList(state));
  };
  return new Plugin({
    key: toolbarStatePluginKey,
    view: (view) => {
      sync(view.state);
      return { update: (view) => sync(view.state) };
    },
  });
}

// Drives the slash menu synchronously from the editor: the plugin view's
// `update` runs after every dispatched transaction (typing, deleting, cursor
// moves), unlike the listener plugin's `updated` event which is debounced
// ~200ms and coalesces transactions. Opens the menu when a doc change leaves
// the caret right after a freshly inserted `/` at a token boundary; while
// open, delegates query refresh / closing to `refreshSlash`.
function createSlashPlugin(
  slashRef: { current: SlashState },
  setSlash: (next: SlashState) => void,
  disableVariablesRef: { current: boolean | undefined }
): Plugin {
  return new Plugin({
    key: slashPluginKey,
    view: () => ({
      update: (view, prevState) => {
        const state = view.state;
        const docChanged = !state.doc.eq(prevState.doc);
        if (!docChanged && state.selection.eq(prevState.selection)) return;

        const s = slashRef.current;
        if (s.open) {
          refreshSlash(state, s, setSlash);
          return;
        }

        if (!docChanged || disableVariablesRef.current) return;
        // Only typing opens the menu. A doc replaced from the form value (the
        // closed run panel mirrors an input's fixed value into its own
        // editor) moves the caret exactly like a typed `/`, so an unfocused
        // editor would otherwise pop a second menu.
        if (!view.hasFocus()) return;
        const sel = state.selection;
        if (!sel.empty) return;
        const slashPos = sel.from - 1;
        // A typed character moves the caret exactly one position forward;
        // this keeps bulk changes (paste of longer text, full-doc replace
        // from the form value sync) from popping the menu.
        if (sel.from !== prevState.selection.from + 1 || slashPos < 0) return;
        if (state.doc.textBetween(slashPos, sel.from) !== "/") return;
        if (!isSlashTriggerContext(state, slashPos)) return;
        try {
          const coords = view.coordsAtPos(slashPos);
          setSlash({
            open: true,
            slashPos,
            x: coords.left,
            y: coords.bottom,
            caretTop: coords.top,
            query: "",
          });
        } catch {
          // coordsAtPos may throw during transitional states; ignore.
        }
      },
    }),
  });
}
